import {
  EnrichmentPreview,
  EnrichmentModelForPreview,
  GlobalMDType,
  MetadataType,
  ModelTypes,
  EnrichmentModel,
} from "types";
import classNames from "classnames";
import { State } from "redux/store";
import { connect } from "react-redux";
import { useParams } from "react-router";
import { useEffect, useMemo, useRef, useState } from "react";

import {
  useWindowResize,
  calculatePlaceHolderCount,
} from "helpers/utility/utilities";
import {
  getAllEnrichmentModelsUsed,
  getEnrichmentPreviewLoading,
  getEnrichmentPreviews,
} from "redux/Enrichment/selectors";
import {
  getIsOwner,
  getIsPublicDataset,
  getEnrichedModelsFromStats,
} from "redux/SingleDataset/selectors";

import ModelList from "./ModelList";
import { ENRICH_PAGE } from "helpers/constants/pages";
import TooltipWrapper from "views/uikit/TooltipWrapper";
import previewInfoIcon from "assets/img/preview-info-icon.svg";
import EnrichmentCard from "views/pages/DataPage/CardsView/DataList/EnrichmentCard";

import styles from "./style.module.scss";

interface PreviewTitleProps {
  previewCount: number;
  isLoading: boolean;
}

const PreviewTitle = ({ previewCount, isLoading }: PreviewTitleProps) => {
  const label = `${previewCount} ${previewCount > 1 ? "Images" : "Image"}`;

  return (
    <div className={styles.previewTextContianer}>
      {isLoading || previewCount > 0 ? `Preview` : ""}
      {!isLoading && previewCount > 0 && (
        <>
          <span className={styles.previewCount}>{label}</span>
          <div
            className={styles.infoIcon}
            data-tooltip-id="previewCount-tooltip"
            data-tooltip-content={`These images are only a quick preview for reviewing the model and its results before running it on the entire dataset. Once you click "Enrich Dataset", the model will run on all your data - note that this process may take some time.`}
            data-tooltip-place="right"
          >
            <img src={previewInfoIcon} alt="preview-info-icon" />
            <TooltipWrapper
              id="previewCount-tooltip"
              customClass="totalQuotaTooltip"
              maxWidth="425px"
            />
          </div>
        </>
      )}
    </div>
  );
};

const PreviewSection = ({
  isOwner,
  previewData,
  onRemoveModel,
  onSelectModel,
  previewModels,
  selectedModelId,
  isPublicDataset,
  isPreviewLoading,
  onShowModelOptions,
  onStartEnrichment,
  enrichedModels,
  allEnrichedModelsUsed,
}: ModelPreviewsProps) => {
  const [placeholderCount, setPlaceholderCount] = useState(0);
  const [indexOfPointerOut, setIndexOfPointerOut] = useState(-1);
  const [globalMDType, setGlobalMDType] = useState<GlobalMDType>({
    type: "IMAGE",
    metadataType: MetadataType.IMAGE_LABELS,
  });

  const previewContainerRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  const { height, width } = useWindowResize();
  const { enrichDatasetId } = useParams();

  const previewSectionClassNames = classNames(styles.previews, {
    [styles.hiddenOverflowY]: previewData.length < 1,
  });

  const hasOnlyEnrichedModels = useMemo((): boolean => {
    let previewModelIds = previewModels.map((model) => model.id);
    let enrichedModelIds = enrichedModels.map((model) => model.id);

    return previewModelIds.every((id) => enrichedModelIds.includes(id));
  }, [previewModels, enrichedModels]);

  //empty functions
  const handleSelect = () => {};
  const handleSetVertex = () => {};
  const onGlobalMDTypeChange = () => {};
  const showPlaceholders = isPreviewLoading && placeholderCount > 0;

  useEffect(() => {
    let type: GlobalMDType["type"] = "IMAGE";
    let metadataType = MetadataType.IMAGE_LABELS;

    const selectedModel = previewModels.find(
      (model) => model.id === selectedModelId
    );

    if (selectedModel?.type) {
      switch (selectedModel.type) {
        case ModelTypes.IMAGE_TAGGING:
          type = "IMAGE";
          metadataType = MetadataType.IMAGE_LABELS;
          break;

        case ModelTypes.CAPTION_IMAGES:
          type = "IMAGE";
          metadataType = MetadataType.IMAGE_CAPTIONS;
          break;

        case ModelTypes.CAPTION_OBJECTS:
          type = "OBJECT";
          metadataType = MetadataType.OBJECT_CAPTIONS;
          break;

        case ModelTypes.OBJECT_DETECTION:
          type = "OBJECT";
          metadataType = MetadataType.OBJECT_LABELS;
          break;

        case ModelTypes.OBJECT_TAGGING:
          type = "OBJECT";
          metadataType = MetadataType.USER_TAGS;
          break;
      }
    }

    setGlobalMDType({ type, metadataType });
  }, [selectedModelId, previewModels]);

  useEffect(() => {
    const extraWidth = 60;

    let tempCount = calculatePlaceHolderCount(
      previewContainerRef.current,
      cardRef.current,
      previewData.length,
      extraWidth
    );
    if (placeholderCount !== tempCount) {
      setPlaceholderCount(tempCount);
    }
  }, [height, width, placeholderCount, previewData.length]);

  const renderPreviewCards = () => {
    const modelsWithoutPreview = previewModels.filter(
      (model) => !model.previews
    );

    const showNoPreview = modelsWithoutPreview.some(
      (model) => model.id === selectedModelId
    );

    if (showNoPreview) {
      return (
        <div className={styles.noPreviewContainer}>
          <div className={styles.noPreviewText}>
            This model doesn’t support a preview. After running the enrichment,
            it will enhance the regular search bar in the dataset explore flow,
            allowing you to discover advanced semantic search results and
            uncover hidden patterns.
          </div>
        </div>
      );
    }

    return (
      <div className={styles.modelPreviewsContainer}>
        <PreviewTitle
          isLoading={isPreviewLoading}
          previewCount={previewData.length}
        />

        <div className={previewSectionClassNames} ref={previewContainerRef}>
          {previewData.map((preview, index) => {
            return (
              <EnrichmentCard
                key={index}
                index={index}
                clusterID={""}
                isVertex={false}
                userTagsData={[]}
                page={ENRICH_PAGE}
                isDataPage={false}
                singleData={preview}
                isClusterPage={true}
                cardElementRef={cardRef}
                showSelectOption={false}
                showMetadataSummary={false}
                datasetId={enrichDatasetId!}
                modelType={globalMDType.type}
                containerRef={previewContainerRef}
                indexOfPointerOut={indexOfPointerOut}
                globalMDType={globalMDType.metadataType}
                onSelect={handleSelect}
                onSetVertex={handleSetVertex}
                onIndexOfPointerOut={setIndexOfPointerOut}
                onGlobalMDTypeChange={onGlobalMDTypeChange}
              />
            );
          })}
          {showPlaceholders &&
            [...Array(placeholderCount)].map((_, index) => (
              <div key={index} className={styles.previewCardPlaceholder}>
                <>
                  <div className={styles.title}>
                    <div className={styles.previewLoader}></div>
                  </div>
                  <div className={styles.description}>
                    <div className={styles.previewLoader}></div>
                  </div>
                  <div className={styles.body}>
                    <div className={styles.previewLoader}></div>
                  </div>
                </>
              </div>
            ))}
        </div>
      </div>
    );
  };

  return (
    <div className={styles.modelPreviewWrapper}>
      <ModelList
        isOwner={isOwner}
        onRemoveModel={onRemoveModel}
        onSelectModel={onSelectModel}
        previewModels={previewModels}
        selectedModelId={selectedModelId}
        isPublicDataset={isPublicDataset}
        onStartEnrichment={onStartEnrichment}
        onShowModelOptions={onShowModelOptions}
        hasOnlyEnrichedModels={hasOnlyEnrichedModels}
        allEnrichedModelsUsed={allEnrichedModelsUsed}
      />

      {renderPreviewCards()}
    </div>
  );
};

const mapStateToProps = (state: State) => {
  return {
    isOwner: getIsOwner(state),
    previewData: getEnrichmentPreviews(state),
    isPublicDataset: getIsPublicDataset(state),
    enrichedModels: getEnrichedModelsFromStats(state),
    isPreviewLoading: getEnrichmentPreviewLoading(state),
    allEnrichedModelsUsed: getAllEnrichmentModelsUsed(state),
  };
};

export default connect(mapStateToProps)(PreviewSection);

interface ModelPreviewsProps {
  isOwner: boolean;
  isPublicDataset: boolean;
  isPreviewLoading: boolean;
  selectedModelId: string | null;
  allEnrichedModelsUsed: boolean;
  previewData: EnrichmentPreview[];
  enrichedModels: EnrichmentModel[];
  previewModels: EnrichmentModelForPreview[];
  onStartEnrichment: () => void;
  onShowModelOptions: () => void;
  onSelectModel: (modelId: string, newPrompt?: boolean) => void;
  onRemoveModel: (modelId: string) => void;
}
